import { useMemo, useState } from "react";
import dynamic from "next/dynamic";
import NoLocationIcon from "../../../../icons/NoLocationIcon";
import { Card } from "../../../../shadcn/components/ui/card";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../../../../shadcn/components/ui/popover";
import { Arrow as PopoverArrow } from "@radix-ui/react-popover";
import { Info } from "lucide-react";

const GoogleMapComponent = dynamic(
  () =>
    import(
      "../../../../shadcn/components/custom/maps/google-maps/GoogleMapComponent"
    ),
  {
    loading: () => (
      <div className="bg-gray-200 rounded-lg w-full h-[220px] animate-pulse" />
    ),
    ssr: false,
  },
);

interface PDMapViewProps {
  isCoordinateFound: boolean;
  position: string[];
  locality: { isFound: boolean; value: string | null };
  city: { name: string };
  state: { name: string };
  area?: string;
}

export function PDMapViewV2({
  isCoordinateFound,
  position,
  locality,
  city,
  state,
  area,
}: PDMapViewProps) {
  const directPosition = useMemo(() => {
    if (isCoordinateFound && position.length === 2) {
      const lat = parseFloat(position[0]);
      const lng = parseFloat(position[1]);
      if (!isNaN(lat) && !isNaN(lng)) {
        return { lat, lng };
      }
    }
    return null;
  }, [isCoordinateFound, position]);
  const [open, setOpen] = useState(false);

  const address =
    locality.isFound && locality.value
      ? `${locality.value}, ${city.name}, ${state.name}, India`
      : null;

  const fallbackAddress = `${city.name}, ${state.name}, India`;

  const isBothAddressUnavailable = !address && !fallbackAddress;

  if (isBothAddressUnavailable) {
    return (
      <Card className="bg-white shadow-card-shadow p-4 border-none rounded-[10px]">
        <h3 className="text-lg font-openSans font-semibold text-brand-textBlack capitalize">
          Property Location
        </h3>
        <div className="flex flex-col justify-center items-center h-[220px] text-center">
          <NoLocationIcon />
          <p className="mt-10 font-openSans text-brand-textBlack text-sm">
            Location data is temporarily unavailable.
          </p>
        </div>
      </Card>
    );
  }
  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            onClick={() => setOpen((v) => !v)}
            className="bg-white flex gap-2 items-center text-sm shadow-card-shadow p-4 border-none rounded-[10px] cursor-pointer"
          >
            <div>
              <Info color="#2563EB" size={20} />
            </div>
            <p>
              Location shown may be indicative and some times represents the
              nearby area/locality
            </p>
          </div>
        </PopoverTrigger>
        <PopoverContent
          side="bottom"
          align="end"
          className="shadow-card-shadow p-4 border-none rounded-[10px] max-w-xs"
        >
          <PopoverArrow />
          <p className="text-sm">
            In some cases, banks disclose only the area/locality in public
            notices. Exact boundaries should be confirmed from title documents
            and site inspection
          </p>
        </PopoverContent>
      </Popover>
      <Card className="bg-white shadow-card-shadow p-4 border-none rounded-[10px]">
        <h3 className="text-lg font-openSans font-semibold text-brand-textBlack capitalize">
          Property Location
        </h3>
        <div className="pt-3">
          {directPosition ? (
            <GoogleMapComponent position={directPosition} area={area} />
          ) : (
            <GoogleMapComponent
              address={address || ""}
              fallbackAddress={fallbackAddress}
              area={area}
            />
          )}
        </div>
      </Card>
    </>
  );
}
